import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  HeartHandshake,
  History as HistoryIcon,
  ExternalLink,
  MapPin,
  QrCode,
  ShieldCheck,
  Tag,
  Trophy,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveData } from "@/lib/realtime";
import { fmtDate } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Tape, Topo } from "@/components/Brand";
import { DonationDialog, QrDialog, WaiverDialog } from "@/components/Dialogs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckinCard, CheckinSuccess, ClimbingNow, NiceSend, PaidRegistrationCard, RoutePicker, WaitingTicket, YourTurn } from "./TicketStates";
import { ClimbersJoining, CommunityFeed, useCommunity } from "./Community";
import { ClimbingGuide } from "./ClimbingGuide";
import { InstallGuide } from "./InstallGuide";
import { CommunityNewsFeed } from "./Feed";
import { EmailRequiredDialog } from "./EmailRequiredDialog";
import { isStandalone, mapsUrl } from "@/lib/image";

function EventPoster({ d, eventId, setEventId, community }) {
  const e = d.event;
  const upcoming = d.event_access === "UPCOMING";
  const paymentPending = e.event_type === "PAID" && !d.checkin;
  const pct = e.participant_quota ? Math.min(100, Math.round((e.checkin_count / e.participant_quota) * 100)) : null;
  return (
    <div
      className="relative overflow-hidden rounded-[22px] bg-[#EFEBDD] p-5 ring-1 ring-[#1A3323]/10 rise rise-1"
      data-testid={upcoming ? "upcoming-event-card" : "event-bar"}
    >
      <Topo className="absolute -right-24 -top-16 h-56 w-96 text-[#3D6F4B]/20" />
      <div className="relative flex items-center justify-between gap-2">
        <Tape>{upcoming ? "Upcoming Event" : e.event_type === "PAID" ? "event" : "Open for Public"}</Tape>
        {upcoming ? (
          <span
            className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#1A3323] ring-1 ring-[#1A3323]/15"
            data-testid="upcoming-event-access-status"
          >
            {e.event_type === "PAID" ? "Sign Up tersedia sekarang" : "Check-in dibuka hari event"}
          </span>
        ) : d.checkin ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#1A3323] px-2.5 py-1 text-[11px] font-bold text-[#FDFCF8]" data-testid="checkin-status"><CheckCircle2 className="h-3.5 w-3.5" /> Sudah check-in</span>
        ) : paymentPending ? (
          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#1A3323] ring-1 ring-[#1A3323]/15" data-testid="checkin-status">Perlu pembayaran</span>
        ) : (
          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#1A3323] ring-1 ring-[#1A3323]/15" data-testid="checkin-status">Belum check-in</span>
        )}
      </div>
      {d.events.length > 1 ? (
        <Select value={eventId || e.id} onValueChange={setEventId}>
          <SelectTrigger className="relative mt-3 h-auto rounded-xl bg-white/70 py-2 text-left text-sm font-extrabold sm:text-base" data-testid="event-select"><SelectValue /></SelectTrigger>
          <SelectContent>{d.events.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {fmtDate(x.date)}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <h2 className="relative mt-3 text-2xl font-extrabold leading-tight tracking-tight text-[#1A3323]" data-testid="event-name">{e.name}</h2>
      )}
      <div className="relative mt-2 space-y-1 text-sm text-[#2A2E2B]/80">
        <div className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtDate(e.date)} · {e.start_time}–{e.end_time}</div>
        <a href={mapsUrl(e)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-semibold text-[#1A3323] underline decoration-[#3D6F4B]/40 underline-offset-2 hover:decoration-[#3D6F4B]" data-testid="event-location-map-link">
          <MapPin className="h-4 w-4 shrink-0" /> <span className="min-w-0">{e.location}</span> <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </a>
        <div className="flex items-center gap-1.5" data-testid="event-type-detail">
          <Tag className="h-4 w-4" />
          {e.event_type === "PAID" ? "Event Berbayar" : "Open for Public"}
        </div>
      </div>
      {upcoming && e.description && (
        <p className="relative mt-3 text-sm leading-relaxed text-[#1A3323]/75" data-testid="upcoming-event-description">
          {e.description}
        </p>
      )}
      {upcoming && e.event_type !== "PAID" && (
        <p className="relative mt-4 rounded-xl bg-white/75 px-3 py-2 text-sm font-semibold text-[#1A3323]" data-testid="upcoming-event-notice">
          Check-in dan tiket baru tersedia pada hari event.
        </p>
      )}
      {!upcoming && <div className="relative mt-4"><ClimbersJoining c={community} /></div>}
      {!upcoming && pct != null && (
        <div className="relative mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-[#3D6F4B]" style={{ width: `${pct}%` }} /></div>
          <div className="mt-1 text-[11px] text-muted-foreground">{e.checkin_count}/{e.participant_quota} kuota climber</div>
        </div>
      )}
    </div>
  );
}

function QuickActions({ onQr, onDonate }) {
  return (
    <div className="space-y-3 rise rise-2">
      <button onClick={onDonate} className="donate-glow grain relative flex w-full items-center gap-4 overflow-hidden rounded-[20px] bg-[#1A3323] p-5 text-left text-[#FDFCF8] ring-2 ring-[#B9D3A8] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[.99]" data-testid="donate-button">
        <Topo className="absolute -right-16 -top-10 h-40 w-64 text-white/10" />
        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#B9D3A8]"><HeartHandshake className="heart-beat h-6 w-6 text-[#1A3323]" /></span>
        <div className="relative flex-1">
          <div className="text-lg font-extrabold leading-tight">Kontribusi Kolektif</div>
          <div className="text-xs text-[#FDFCF8]/75">Dukung wall & alat komunitas lewat QRIS</div>
        </div>
        <span className="relative rounded-full bg-[#FDFCF8] px-3 py-1.5 text-xs font-extrabold text-[#1A3323]">Dukung</span>
      </button>
      <button onClick={onQr} className="flex min-h-[56px] w-full items-center gap-3 rounded-[18px] bg-white p-4 text-left ring-1 ring-[#1A3323]/10 transition-colors hover:bg-[#EFEBDD]" data-testid="show-qr-button">
        <QrCode className="h-5 w-5 text-[#1A3323]" /><div className="flex-1"><div className="font-bold text-[#1A3323]">QR Saya</div><div className="text-xs text-muted-foreground">tunjukkan ke keeper</div></div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
}

const LinkCard = ({ to, icon: Icon, title, desc, id }) => (
  <Link to={to} data-testid={id} className="flex min-h-[56px] items-center gap-3 rounded-[18px] bg-white p-4 ring-1 ring-[#1A3323]/10 transition-colors hover:bg-[#EFEBDD]">
    <Icon className="h-5 w-5 text-[#3D6F4B]" />
    <div className="flex-1"><div className="font-bold text-[#1A3323]">{title}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
  </Link>
);

const seen = (id) => sessionStorage.getItem(`iym_seen_${id}`);
const markSeen = (id) => sessionStorage.setItem(`iym_seen_${id}`, "1");

function MainState({ d, offset, load, openWaiver, justIn, setJustIn }) {
  const [, force] = useState(0);
  const p = d.profile;
  if (!d.waiver_ok) {
    return (
      <div className="rounded-[26px] bg-white p-6 ring-2 ring-[#1A3323]" data-testid="waiver-required-card">
        <ShieldCheck className="h-8 w-8 text-[#1A3323]" />
        <h3 className="mt-2 text-xl font-extrabold text-[#1A3323]">Waiver belum disetujui</h3>
        <p className="text-sm text-muted-foreground">Ada waiver versi {d.waiver?.version} yang perlu kamu setujui dulu sebelum manjat.</p>
        <Button className="mt-4 h-12 w-full rounded-xl font-bold" onClick={openWaiver} data-testid="open-waiver-button">Baca & Setujui</Button>
      </div>
    );
  }
  if (!d.event) return <Empty title="Belum ada sesi aktif" desc="Pantau info sesi berikutnya dari komunitas. Riwayat & leaderboard tetap bisa dilihat." />;
  if (d.event_access === "UPCOMING" && d.event.event_type !== "PAID") return null;
  if (
    d.event.event_type === "PAID"
    && (d.event_access === "UPCOMING" || !d.checkin)
  ) {
    return (
      <PaidRegistrationCard
        event={d.event}
        profile={p}
        registration={d.registration}
        paidWaiverOk={d.paid_waiver_ok}
        eventAccess={d.event_access}
        onDone={load}
      />
    );
  }
  if (!d.checkin) return <CheckinCard event={d.event} profile={p} onDone={() => { setJustIn(true); load(); }} />;
  if (justIn && !d.ticket) return <CheckinSuccess onNext={() => setJustIn(false)} />;
  const t = d.ticket;
  if (t?.status === "WAITING") return <WaitingTicket t={t} onChange={load} />;
  if (t?.status === "CLIMBING") return <ClimbingNow t={t} offset={offset} />;
  if (t) return <YourTurn t={t} />;
  const last = d.last_ticket;
  if (last?.status === "COMPLETED" && !seen(last.id)) {
    return <NiceSend t={last} eventName={d.event.name} onNext={() => { markSeen(last.id); force((x) => x + 1); }} />;
  }
  return <RoutePicker routes={d.routes} profile={p} event={d.event} lastTicket={last} onDone={load} />;
}

export default function Dashboard() {
  const { activeProfile, refresh } = useAuth();
  const [eventId, setEventId] = useState(null);
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");
  const [qr, setQr] = useState(false);
  const [donate, setDonate] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [justIn, setJustIn] = useState(false);
  const [donateReminder, setDonateReminder] = useState(false);
  const community = useCommunity(d?.event?.id, tick);
  const lastDone = d?.last_ticket?.status === "COMPLETED" ? d.last_ticket.id : null;

  useEffect(() => {
    if (lastDone && !localStorage.getItem(`iym_donate_${lastDone}`)) {
      localStorage.setItem(`iym_donate_${lastDone}`, "1");
      setDonateReminder(true);
    }
  }, [lastDone]);

  const selectEvent = (nextEventId) => {
    if (nextEventId === eventId) {
      return;
    }
    setD(null);
    setEventId(nextEventId);
  };

  const load = async () => {
    if (!activeProfile) return;
    try {
      const { data } = await api.get("/me/dashboard", { params: { profile_id: activeProfile.id, event_id: eventId || undefined } });
      setOffset(Date.parse(data.server_time) - Date.now());
      setD(data);
      setTick((x) => x + 1);
      setError("");
    } catch (e) {
      setError(errMsg(e));
    }
  };
  useLiveData(load, [activeProfile?.id, eventId]);

  if (!d) return error ? <p className="text-sm text-red-700">{error}</p> : <div className="space-y-3"><Skeleton className="h-40 rounded-[22px]" /><Skeleton className="h-56 rounded-[26px]" /></div>;
  const p = d.profile;
  return (
    <div className="space-y-4">
      <div className="rise">
        <p className="text-sm font-semibold text-[#3D6F4B]">Halo, climber!</p>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#1A3323]" data-testid="participant-name">{p.display_name || p.full_name}</h1>
      </div>
      {d.event && <EventPoster d={d} eventId={eventId} setEventId={selectEvent} community={community} />}
      <MainState d={d} offset={offset} load={load} openWaiver={() => setWaiverOpen(true)} justIn={justIn} setJustIn={setJustIn} />
      {d.checkin && <div className="text-center text-xs font-semibold text-[#1A3323]/70" data-testid="completed-in-event">{d.completed_in_event} climb selesai di sesi ini</div>}
      <QuickActions onQr={() => setQr(true)} onDonate={() => setDonate(true)} />
      <CommunityNewsFeed />
      {d.event_access !== "UPCOMING" && <CommunityFeed c={community} />}
      <div className="space-y-2 rise rise-3" data-testid="home-quick-links">
        <LinkCard to="/app/riwayat" icon={HistoryIcon} title="Riwayat Climbing" desc="Semua climb yang sudah selesai" id="home-history-link" />
        <LinkCard to="/app/leaderboard" icon={Trophy} title="Leaderboard" desc="Climber paling rajin" id="home-leaderboard-link" />
        {!isStandalone() && <InstallGuide />}
      </div>
      <ClimbingGuide />
      <QrDialog open={qr} onOpenChange={setQr} profileId={p.id} />
      <DonationDialog open={donate} onOpenChange={setDonate} eventId={d.event?.id} />
      <DonationDialog open={donateReminder} onOpenChange={setDonateReminder} eventId={d.event?.id} reminder />
      <EmailRequiredDialog />
      <WaiverDialog open={waiverOpen} onOpenChange={setWaiverOpen} waiver={d.waiver} profile={p} onAccepted={() => { load(); refresh(); }} />
    </div>
  );
}
